import { observable } from "mobx";
import { api, apiOperacao } from "../../api";
import { toast } from "react-toastify";
import { decodeUtf8String, downloadBinaryFile, toast_error } from "../../utils";

export class SpedImportExcel {
  obs = observable({
    importarTxts: false as boolean,
    importarExcel: false as boolean,
    loadingImport: false as boolean,
    loading: false as boolean,
    tasks_sped: [] as any[],
    length_tasks_sped: 0 as number
  })

  constructor() {
  }

  /**
   * Realiza o download de um arquivo de relatório SPED e salva.
   *
   * Envia uma requisição `POST` para o endpoint de download com o `id` do relatório
   * e o `format` desejado, esperando uma resposta binária (`blob`).
   * Em caso de sucesso (`status 200`), monta o nome do arquivo no padrão
   * `{cnpj}_relatorio.{format}` e dispara o download via `downloadBinaryFile`.
   *
   * Durante a execução, controla o estado de carregamento por meio de
   * `this.obs.loading`, permitindo exibir feedback visual no front (ex.: spinner).
   * Em caso de erro de resposta ou exceção, exibe mensagens ao usuário com `toast.error`.
   *
   * @async
   * @function handleDownloadArquivoRelatorio
   * @param {string} id - Identificador do relatório a ser baixado.
   * @param {string} cnpj - CNPJ utilizado para compor o nome final do arquivo.
   * @param {string} format - Formato/extensão do arquivo a ser baixado (ex.: `txt`, `xlsx`).
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é o download
   *   do arquivo no navegador e a atualização do estado de loading.
  */

  async handleDownloadArquivoRelatorio(id: string, cnpj: string, format: string) {
    
    this.obs.loading = true

    try {

      let resp = await apiOperacao("relatorios").post(`/relatorios/task/download_file_sped`, {id: id, format: format}, { responseType: 'blob' });

      if(resp.status == 200){

        const fileName = cnpj + "_relatorio." + format;

        downloadBinaryFile(resp.data, fileName)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao baixar o arquivo, tente novamente mais tarde`);

    }

    this.obs.loading = false
  }

  /**
   * Realiza a importação de um arquivo Excel SPED por meio da API e exibe feedback ao usuário.
   *
   * Envia uma requisição `POST` para o endpoint de importação com os dados recebidos em `data`.
   * Durante o processo, controla o estado de carregamento em `this.obs.loadingImport`.
   * Após a resposta:
   * - Se `resp.data.ok` for `false`, exibe aviso com a mensagem retornada pela API.
   * - Caso contrário, exibe mensagem de sucesso informando que a importação foi concluída.
   *
   * Ao final da tentativa (com sucesso ou erro), o fluxo desativa o loading.
   * Em caso de execução sem erro, também fecha/oculta a interface de importação
   * definindo `this.obs.importarExcel = false`.
   *
   * @async
   * @function handldeCreateTaskImportExcel
   * @param {any} data - Payload enviado para a API de importação. Geralmente contém os dados
   *   necessários para processar o arquivo Excel SPED (ex.: arquivo, metadados, parâmetros).
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é disparar a
   *   importação no backend, atualizar estados da interface e exibir mensagens via toast.
  */

  async handldeCreateTaskImportExcel(data) {

    this.obs.loadingImport = true

    try {
      let resp = await apiOperacao("relatorios").post("/relatorios/import-excel-sped", data);

      if (resp.data.ok == false) {
        toast.warning(resp.data.erro, {});
      } else {
        toast.success('Importado com sucesso!', {});
      }

      this.obs.importarExcel = false;

    } catch (ex) {

      toast_error(ex, `Erro ao realizar importação, tente novamente mais tarde`);

    }

    this.obs.loadingImport = false

  }

  /**
   * Consulta a lista de tarefas/relatórios SPED -> TXT paginados por CNPJ e atualiza o estado da tela.
   *
   * Monta um payload com `cnpj`, página atual e limite fixo de 15 registros por página,
   * enviando uma requisição `POST` para o endpoint de consulta. Após receber a resposta,
   * limpa a lista atual (`this.obs.tasks_sped`) e o total (`this.obs.length_tasks_sped`),
   * preenchendo novamente esses estados apenas se houver itens retornados.
   *
   * Em caso de falha na requisição ou erro inesperado, exibe uma mensagem de erro ao usuário
   * por meio de `toast.error`.
   *
   * @async
   * @function handleConsulta
   * @param {number} paged - Número da página a ser consultada.
   * @param {string} cnpj - CNPJ utilizado como filtro da consulta.
   * @returns {Promise<void>} Promise assíncrona sem retorno. O efeito esperado é atualizar
   *   a listagem de tarefas SPED TXT e a quantidade total de registros no estado observável.
  */

  async handleConsulta(paged: number, cnpj: string) {

    try {

      let data = {
        cnpj: cnpj,
        page: paged,
        limit: 15
      }

      let resp = await apiOperacao("relatorios").post("/relatorios/index-sped-txt", data);

      this.obs.tasks_sped = [];
      this.obs.length_tasks_sped = 0

      if (resp.data.itens.length > 0) {
        this.obs.tasks_sped = resp.data.itens
        this.obs.length_tasks_sped = resp.data.count
      }

    } catch (ex) {

      toast_error(ex, `Erro ao consultar tarefas, tente novamente mais tarde`);

    }

  }
}

export const SpedImportExcelStore = new SpedImportExcel();
